import React, { useEffect, useState, useCallback, useRef } from "react";
import api, { fcfa } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, Cell } from "recharts";
import {
  Users, Store, Wrench, Package, Receipt, ShieldCheck,
  CheckCircle2, XCircle, Pencil, Trash2, Save, X,
  UtensilsCrossed, BedDouble, Car, CreditCard, Search, ImageIcon, Upload,
  UserPlus, MapPin, TrendingUp, Award, Clock, ClipboardList, ShieldAlert,
} from "lucide-react";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState({ shops: [], providers: [] });
  const [txs, setTxs] = useState([]);
  const [users, setUsers] = useState([]);
  // Contenu
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [shops, setShops] = useState([]);
  const [prodSearch, setProdSearch] = useState("");
  const [editProd, setEditProd] = useState(null);
  // Souscriptions
  const [subs, setSubs] = useState([]);
  const [plans, setPlans] = useState([]);
  const [editSub, setEditSub] = useState(null);
  // Restaurants / Hébergements / Transports
  const [restos, setRestos] = useState([]);
  const [hebergs, setHebergs] = useState([]);
  const [transports, setTransports] = useState([]);
  // Agents
  const [agentStats, setAgentStats] = useState(null);
  const [newAgent, setNewAgent] = useState({ name: "", email: "", password: "", phone: "" });
  const [agentBusy, setAgentBusy] = useState(false);
  // Validation terrain & candidatures
  const [brouillons, setBrouillons] = useState({ shops: [], restaurants: [], hebergements: [], transports: [], providers: [], products: [], total: 0 });
  const [candidatures, setCandidatures] = useState([]);
  const [candBusy, setCandBusy] = useState({});

  const load = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/pending").then((r) => setPending(r.data)).catch(() => {});
    api.get("/admin/transactions").then((r) => setTxs(r.data)).catch(() => {});
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/admin/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/admin/shops").then((r) => setShops(r.data)).catch(() => {});
    api.get("/admin/subscriptions").then((r) => setSubs(r.data)).catch(() => {});
    api.get("/subscriptions/plans").then((r) => setPlans(r.data)).catch(() => {});
    api.get("/admin/restaurants").then((r) => setRestos(r.data)).catch(() => {});
    api.get("/admin/hebergements").then((r) => setHebergs(r.data)).catch(() => {});
    api.get("/admin/transports").then((r) => setTransports(r.data)).catch(() => {});
    api.get("/admin/agent/stats").then((r) => setAgentStats(r.data)).catch(() => {});
    api.get("/admin/brouillons").then((r) => setBrouillons(r.data)).catch(() => {});
    api.get("/admin/agent/candidatures").then((r) => setCandidatures(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const validerBrouillon = async (type, id, action) => {
    await api.patch(`/admin/brouillons/${type}/${id}?action=${action}`);
    toast.success(action === "approuver" ? "✅ Approuvé — visible sur le site" : "❌ Rejeté");
    load();
  };

  const updateCandidature = async (candId, statut) => {
    setCandBusy((p) => ({ ...p, [candId]: true }));
    try {
      await api.patch(`/admin/agent/candidatures/${candId}?statut=${statut}`);
      toast.success(statut === "validee" ? "✅ Candidature validée" : statut === "refusee" ? "❌ Candidature refusée" : "Mis à jour");
      load();
    } finally { setCandBusy((p) => ({ ...p, [candId]: false })); }
  };

  const decide = async (type, id, decision) => {
    await api.post("/admin/approve", { type, id, decision });
    toast.success(decision === "approved" ? "Validé" : "Refusé");
    load();
  };

  const approveVertical = async (endpoint, id, status) => {
    await api.post(`/admin/${endpoint}/${id}/${status === "approved" ? "approve" : "reject"}`);
    toast.success(status === "approved" ? "Validé" : "Refusé");
    load();
  };

  const saveProd = async () => {
    if (!editProd) return;
    await api.put(`/admin/products/${editProd.product_id}`, {
      name: editProd.name,
      price: parseFloat(editProd.price),
      image_url: editProd.image_url,
    });
    toast.success("Produit mis à jour");
    setEditProd(null);
    load();
  };

  const deleteProd = async (id) => {
    if (!window.confirm("Supprimer ce produit ?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Supprimé");
    load();
  };

  const uploadImage = async (file) => {
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/admin/upload-image", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = data.url || data.file_url || data.image_url || "";
      if (url) {
        setEditProd((prev) => ({ ...prev, image_url: url }));
        toast.success("Image téléchargée");
      } else {
        throw new Error("URL manquante dans la réponse");
      }
    } catch {
      // Fallback : prévisualisation locale (data URI)
      const reader = new FileReader();
      reader.onload = (ev) => {
        setEditProd((prev) => ({ ...prev, image_url: ev.target.result }));
        toast.info("Image chargée localement (endpoint upload indisponible)");
      };
      reader.readAsDataURL(file);
    }
  };

  const saveSub = async () => {
    if (!editSub) return;
    await api.put(`/admin/subscriptions/${editSub.user_id}`, {
      plan: editSub.plan,
      end_date: editSub.end_date,
    });
    toast.success("Souscription mise à jour");
    setEditSub(null);
    load();
  };

  const createAgent = async () => {
    if (!newAgent.name || !newAgent.email || !newAgent.password) {
      toast.error("Nom, email et mot de passe requis"); return;
    }
    setAgentBusy(true);
    try {
      await api.post("/admin/agents", newAgent);
      toast.success(`Agent ${newAgent.name} créé`);
      setNewAgent({ name: "", email: "", password: "", phone: "" });
      api.get("/admin/agent/stats").then((r) => setAgentStats(r.data)).catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur création agent");
    } finally { setAgentBusy(false); }
  };

  const deactivateAgent = async (uid, name) => {
    if (!window.confirm(`Désactiver ${name} ?`)) return;
    await api.delete(`/admin/agents/${uid}`);
    toast.success("Agent désactivé");
    api.get("/admin/agent/stats").then((r) => setAgentStats(r.data)).catch(() => {});
  };

  const [detailModal, setDetailModal] = useState(null); // { type, entity, products }
  const openDetail = async (type, entity) => {
    let prods = [];
    if (type === "shop") {
      try { const r = await api.get(`/agent/shops/${entity.shop_id}/products`); prods = r.data; } catch {}
    }
    setDetailModal({ type, entity, products: prods });
  };

  if (!stats) return <div className="text-center py-20 text-muted-foreground">Chargement…</div>;

  const kpis = [
    { icon: Users, label: "Utilisateurs", value: stats.users },
    { icon: Store, label: "Boutiques", value: stats.shops },
    { icon: Wrench, label: "Prestataires", value: stats.providers },
    { icon: Package, label: "Produits", value: stats.products },
    { icon: Receipt, label: "Revenus", value: fcfa(stats.revenue) },
    { icon: ShieldCheck, label: "RDV", value: stats.appointments },
  ];
  const chartData = Object.entries(stats.by_role || {}).map(([k, v]) => ({ name: k, value: v }));
  const colors = ["#FF6B00", "#00BFA6", "#1A2B4C", "#F59E0B"];
  const pendingCount = pending.shops.length + pending.providers.length;
  const pendingRestos = restos.filter((r) => r.status === "pending");
  const pendingHebergs = hebergs.filter((h) => h.status === "pending");
  const pendingTransports = transports.filter((t) => t.status === "pending");
  const totalPending = pendingCount + pendingRestos.length + pendingHebergs.length + pendingTransports.length;

  const filteredProds = products.filter((p) =>
    !prodSearch || p.name?.toLowerCase().includes(prodSearch.toLowerCase())
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-secondary">Back-office administrateur</h1>
      <p className="text-muted-foreground">Supervision de la plateforme LesMeilleursPrix.</p>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mt-6">
        {kpis.map((k, i) => (
          <div key={i} className="bg-card border border-border rounded-lg p-4">
            <k.icon className="w-5 h-5 text-primary mb-2" />
            <div className="font-display text-xl font-bold text-secondary truncate">{k.value}</div>
            <div className="text-xs text-muted-foreground">{k.label}</div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="moderation" className="mt-8">
        <TabsList className="flex-wrap h-auto gap-1">
          <TabsTrigger value="moderation">
            Modération {totalPending > 0 && <Badge className="ml-1 bg-primary text-white text-xs">{totalPending}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="contenu">Contenu</TabsTrigger>
          <TabsTrigger value="souscriptions">Souscriptions</TabsTrigger>
          <TabsTrigger value="stats">Statistiques</TabsTrigger>
          <TabsTrigger value="tx">Transactions</TabsTrigger>
          <TabsTrigger value="users">Utilisateurs</TabsTrigger>
          <TabsTrigger value="agents" className="flex items-center gap-1">
            <UserPlus className="w-3.5 h-3.5" />Agents
            {agentStats?.agents?.length > 0 && <Badge className="ml-1 bg-green-600 text-white text-xs">{agentStats.agents.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="terrain" className="flex items-center gap-1">
            <ShieldAlert className="w-3.5 h-3.5" />Validation terrain
            {brouillons.total > 0 && <Badge className="ml-1 bg-amber-500 text-white text-xs">{brouillons.total}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="candidatures" className="flex items-center gap-1">
            <ClipboardList className="w-3.5 h-3.5" />Candidatures
            {candidatures.filter(c => c.statut === "en_attente").length > 0 && (
              <Badge className="ml-1 bg-blue-600 text-white text-xs">{candidatures.filter(c => c.statut === "en_attente").length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* ─── MODÉRATION ─── */}
        <TabsContent value="moderation" className="mt-4 space-y-6">
          <Section title="Boutiques en attente" empty="Aucune boutique à valider">
            {pending.shops.map((s) => (
              <PendingShopCard key={s.shop_id} shop={s}
                onApprove={() => decide("shop", s.shop_id, "approved")}
                onReject={() => decide("shop", s.shop_id, "rejected")}
                onDetail={() => openDetail("shop", s)} />
            ))}
          </Section>
          <Section title="Prestataires en attente" empty="Aucun prestataire à valider">
            {pending.providers.map((p) => (
              <PendingProviderCard key={p.provider_id} provider={p}
                onApprove={() => decide("provider", p.provider_id, "approved")}
                onReject={() => decide("provider", p.provider_id, "rejected")}
                onDetail={() => openDetail("provider", p)} />
            ))}
          </Section>
          <Section title={<><UtensilsCrossed className="w-4 h-4 inline mr-1" />Restaurants en attente</>} empty="Aucun restaurant à valider">
            {pendingRestos.map((r) => (
              <Row key={r.restaurant_id} title={r.name} sub={`${r.cuisine} · ${r.city}`}
                onApprove={() => approveVertical("restaurants", r.restaurant_id, "approved")}
                onReject={() => approveVertical("restaurants", r.restaurant_id, "rejected")} tid={r.restaurant_id} />
            ))}
          </Section>
          <Section title={<><BedDouble className="w-4 h-4 inline mr-1" />Hébergements en attente</>} empty="Aucun hébergement à valider">
            {pendingHebergs.map((h) => (
              <Row key={h.hebergement_id} title={h.name} sub={`${h.type} · ${h.city}`}
                onApprove={() => approveVertical("hebergements", h.hebergement_id, "approved")}
                onReject={() => approveVertical("hebergements", h.hebergement_id, "rejected")} tid={h.hebergement_id} />
            ))}
          </Section>
          <Section title={<><Car className="w-4 h-4 inline mr-1" />Transports en attente</>} empty="Aucun transport à valider">
            {pendingTransports.map((t) => (
              <Row key={t.transport_id} title={t.nom} sub={`${t.type} · ${t.ville}`}
                onApprove={() => approveVertical("transports", t.transport_id, "approved")}
                onReject={() => approveVertical("transports", t.transport_id, "rejected")} tid={t.transport_id} />
            ))}
          </Section>
        </TabsContent>

        {/* ─── CONTENU ─── */}
        <TabsContent value="contenu" className="mt-4 space-y-8">
          {/* Produits */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-secondary">Produits ({products.length})</h3>
              <div className="relative w-56">
                <Search className="absolute left-2 top-2.5 w-4 h-4 text-muted-foreground" />
                <Input className="pl-8 h-9" placeholder="Rechercher…" value={prodSearch} onChange={(e) => setProdSearch(e.target.value)} />
              </div>
            </div>
            <div className="bg-card border border-border rounded-xl overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted text-muted-foreground text-left">
                  <tr>
                    <th className="p-3 w-14">Image</th>
                    <th className="p-3">Nom</th>
                    <th className="p-3">Prix</th>
                    <th className="p-3">Catégorie</th>
                    <th className="p-3">Boutique</th>
                    <th className="p-3 w-20">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProds.slice(0, 50).map((p) => (
                    <tr key={p.product_id} className="border-t border-border hover:bg-muted/30">
                      {editProd?.product_id === p.product_id ? (
                        <td colSpan={6} className="p-3">
                          <div className="flex flex-col gap-3">
                            <div className="flex gap-3 items-start flex-wrap">
                              {/* Prévisualisation image */}
                              <div className="w-20 h-20 rounded-lg border border-border bg-muted flex items-center justify-center shrink-0 overflow-hidden">
                                {editProd.image_url ? (
                                  <img src={editProd.image_url} alt="" className="w-full h-full object-contain" onError={(e) => { e.target.style.display="none"; }} />
                                ) : (
                                  <ImageIcon className="w-7 h-7 text-muted-foreground/40" />
                                )}
                              </div>
                              <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <div>
                                  <label className="text-xs text-muted-foreground mb-1 block">Nom</label>
                                  <Input value={editProd.name} onChange={(e) => setEditProd({ ...editProd, name: e.target.value })} className="h-8" />
                                </div>
                                <div>
                                  <label className="text-xs text-muted-foreground mb-1 block">Prix (FCFA)</label>
                                  <Input type="number" value={editProd.price} onChange={(e) => setEditProd({ ...editProd, price: e.target.value })} className="h-8" />
                                </div>
                                <div className="sm:col-span-2">
                                  <label className="text-xs text-muted-foreground mb-1 block">Image</label>
                                  <div className="flex gap-2 items-center">
                                    <Input value={editProd.image_url || ""} placeholder="https://lmp.sag.ci/lmp-images/produit.jpg" onChange={(e) => setEditProd({ ...editProd, image_url: e.target.value })} className="h-8 font-mono text-xs flex-1" />
                                    <label className="cursor-pointer">
                                      <input type="file" accept="image/*" className="hidden"
                                        onChange={(e) => { if (e.target.files[0]) uploadImage(e.target.files[0]); }} />
                                      <span className="inline-flex items-center gap-1 h-8 px-3 rounded-md border border-border bg-muted hover:bg-muted/80 text-xs font-medium text-secondary transition-colors">
                                        <Upload className="w-3.5 h-3.5" />Parcourir
                                      </span>
                                    </label>
                                  </div>
                                  <p className="text-[10px] text-muted-foreground mt-1">Collez une URL ou cliquez Parcourir pour uploader un fichier (JPG, PNG, WEBP)</p>
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button size="sm" className="bg-accent text-accent-foreground" onClick={saveProd}><Save className="w-3.5 h-3.5 mr-1" />Enregistrer</Button>
                              <Button size="sm" variant="outline" onClick={() => setEditProd(null)}><X className="w-3.5 h-3.5 mr-1" />Annuler</Button>
                            </div>
                          </div>
                        </td>
                      ) : (
                        <>
                          <td className="p-2">
                            <div className="w-10 h-10 rounded-md border border-border bg-muted flex items-center justify-center overflow-hidden">
                              {p.image_url ? (
                                <img src={p.image_url} alt="" className="w-full h-full object-contain" onError={(e) => { e.target.style.display="none"; e.target.nextSibling.style.display="flex"; }} />
                              ) : null}
                              <ImageIcon className="w-4 h-4 text-muted-foreground/40" style={{ display: p.image_url ? "none" : "block" }} />
                            </div>
                          </td>
                          <td className="p-3 font-medium max-w-[180px] truncate">{p.name}</td>
                          <td className="p-3 font-mono">{fcfa(p.price)}</td>
                          <td className="p-3 text-muted-foreground">{p.category_name}</td>
                          <td className="p-3 text-muted-foreground">{p.shop_name}</td>
                          <td className="p-3 flex gap-1">
                            <Button size="icon" variant="outline" className="w-7 h-7" onClick={() => setEditProd({ ...p })}><Pencil className="w-3.5 h-3.5" /></Button>
                            <Button size="icon" variant="outline" className="w-7 h-7 text-red-500 hover:text-red-600" onClick={() => deleteProd(p.product_id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                  {filteredProds.length === 0 && (
                    <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Aucun produit trouvé.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Catégories */}
          <div>
            <h3 className="font-semibold text-secondary mb-3">Catégories ({categories.length})</h3>
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <div key={c.category_id} className="flex items-center gap-2 bg-card border border-border rounded-lg px-3 py-2 text-sm">
                  <span>{c.icon}</span>
                  <span className="font-medium text-secondary">{c.name}</span>
                  <Badge variant="secondary" className="text-xs">{c.product_count ?? "—"}</Badge>
                </div>
              ))}
              {categories.length === 0 && <p className="text-muted-foreground text-sm">Aucune catégorie.</p>}
            </div>
          </div>

          {/* Boutiques */}
          <div>
            <h3 className="font-semibold text-secondary mb-3">Boutiques ({shops.length})</h3>
            <div className="bg-card border border-border rounded-xl overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted text-muted-foreground text-left">
                  <tr><th className="p-3">Nom</th><th className="p-3">Ville</th><th className="p-3">Catégorie</th><th className="p-3">Statut</th></tr>
                </thead>
                <tbody>
                  {shops.map((s) => (
                    <tr key={s.shop_id} className="border-t border-border">
                      <td className="p-3 font-medium">{s.name}</td>
                      <td className="p-3 text-muted-foreground">{s.city}</td>
                      <td className="p-3 text-muted-foreground">{s.category}</td>
                      <td className="p-3">
                        <Badge className={s.status === "approved" ? "bg-accent text-accent-foreground" : s.status === "pending" ? "bg-amber-500 text-white" : "bg-muted text-muted-foreground"}>
                          {s.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                  {shops.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-muted-foreground">Aucune boutique.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* ─── SOUSCRIPTIONS ─── */}
        <TabsContent value="souscriptions" className="mt-4 space-y-6">
          {/* Plans */}
          <div>
            <h3 className="font-semibold text-secondary mb-3 flex items-center gap-2"><CreditCard className="w-4 h-4" /> Plans disponibles</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {plans.map((pl) => (
                <div key={pl.plan_id} className="bg-card border border-border rounded-xl p-4">
                  <div className="font-display font-bold text-secondary">{pl.nom}</div>
                  <div className="text-2xl font-bold text-primary mt-1">{fcfa(pl.prix_mensuel)}<span className="text-sm font-normal text-muted-foreground">/mois</span></div>
                  <ul className="mt-3 space-y-1">
                    {(pl.features || []).map((f, i) => (
                      <li key={i} className="text-xs text-muted-foreground flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-accent shrink-0" />{f}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Souscripteurs */}
          <div>
            <h3 className="font-semibold text-secondary mb-3">Abonnés actifs ({subs.length})</h3>
            <div className="bg-card border border-border rounded-xl overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted text-muted-foreground text-left">
                  <tr>
                    <th className="p-3">Utilisateur</th>
                    <th className="p-3">Email</th>
                    <th className="p-3">Plan</th>
                    <th className="p-3">Statut</th>
                    <th className="p-3">Expire le</th>
                    <th className="p-3 w-24">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {subs.map((s) => (
                    <tr key={s.subscription_id} className="border-t border-border">
                      {editSub?.user_id === s.user_id ? (
                        <>
                          <td className="p-2" colSpan={2}><span className="text-muted-foreground text-xs">{s.name} — {s.email}</span></td>
                          <td className="p-2">
                            <select className="border border-border rounded px-2 py-1 text-sm bg-background text-foreground"
                              value={editSub.plan} onChange={(e) => setEditSub({ ...editSub, plan: e.target.value })}>
                              {plans.map((pl) => <option key={pl.code} value={pl.code}>{pl.nom}</option>)}
                            </select>
                          </td>
                          <td className="p-2 text-muted-foreground">{s.status}</td>
                          <td className="p-2"><Input type="date" value={editSub.end_date?.slice(0, 10) || ""} onChange={(e) => setEditSub({ ...editSub, end_date: e.target.value })} className="h-8 w-36" /></td>
                          <td className="p-2 flex gap-1">
                            <Button size="icon" className="w-7 h-7 bg-accent text-accent-foreground" onClick={saveSub}><Save className="w-3.5 h-3.5" /></Button>
                            <Button size="icon" variant="outline" className="w-7 h-7" onClick={() => setEditSub(null)}><X className="w-3.5 h-3.5" /></Button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="p-3 font-medium">{s.name}</td>
                          <td className="p-3 text-muted-foreground">{s.email}</td>
                          <td className="p-3"><Badge variant="secondary" className="capitalize">{s.plan}</Badge></td>
                          <td className="p-3"><Badge className={s.status === "active" ? "bg-accent text-accent-foreground" : "bg-muted text-muted-foreground"}>{s.status}</Badge></td>
                          <td className="p-3 text-muted-foreground">{s.end_date ? new Date(s.end_date).toLocaleDateString("fr-FR") : "—"}</td>
                          <td className="p-3">
                            <Button size="icon" variant="outline" className="w-7 h-7" onClick={() => setEditSub({ ...s })}><Pencil className="w-3.5 h-3.5" /></Button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                  {subs.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Aucun abonné.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* ─── STATS ─── */}
        <TabsContent value="stats" className="mt-4">
          <div className="bg-card border border-border rounded-xl p-6">
            <h3 className="font-semibold text-secondary mb-4">Répartition des utilisateurs par rôle</h3>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartData}>
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {chartData.map((_, i) => <Cell key={i} fill={colors[i % colors.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </TabsContent>

        {/* ─── TRANSACTIONS ─── */}
        <TabsContent value="tx" className="mt-4">
          <div className="bg-card border border-border rounded-xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted text-muted-foreground text-left"><tr>
                <th className="p-3">Client</th><th className="p-3">Type</th><th className="p-3">Méthode</th><th className="p-3">Montant</th><th className="p-3">Statut</th>
              </tr></thead>
              <tbody>
                {txs.map((t) => (
                  <tr key={t.tx_id} className="border-t border-border">
                    <td className="p-3">{t.user_name}</td>
                    <td className="p-3 capitalize">{t.kind}</td>
                    <td className="p-3">{t.method}</td>
                    <td className="p-3 font-medium">{fcfa(t.amount)}</td>
                    <td className="p-3"><Badge className={t.status === "paid" ? "bg-accent text-accent-foreground" : "bg-amber-500 text-white"}>{t.status}</Badge></td>
                  </tr>
                ))}
                {txs.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">Aucune transaction.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* ─── UTILISATEURS ─── */}
        <TabsContent value="users" className="mt-4">
          <div className="bg-card border border-border rounded-xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted text-muted-foreground text-left"><tr>
                <th className="p-3">Nom</th><th className="p-3">Email</th><th className="p-3">Rôle</th><th className="p-3">Statut</th>
              </tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} className="border-t border-border">
                    <td className="p-3">{u.name}</td><td className="p-3">{u.email}</td>
                    <td className="p-3"><Badge variant="secondary" className="capitalize">{u.role}</Badge></td>
                    <td className="p-3"><Badge className={u.status === "active" ? "bg-accent text-accent-foreground" : "bg-amber-500 text-white"}>{u.status}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* ─── AGENTS ─── */}
        <TabsContent value="agents" className="mt-4 space-y-6">
          {/* KPIs agents */}
          {agentStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-card border border-border rounded-xl p-4 text-center">
                <div className="font-display text-2xl font-bold text-secondary">{agentStats.agents?.length || 0}</div>
                <div className="text-xs text-muted-foreground mt-1 flex items-center justify-center gap-1"><UserPlus className="w-3 h-3" />Agents actifs</div>
              </div>
              <div className="bg-card border border-border rounded-xl p-4 text-center">
                <div className="font-display text-2xl font-bold text-primary">{agentStats.total_visits || 0}</div>
                <div className="text-xs text-muted-foreground mt-1 flex items-center justify-center gap-1"><MapPin className="w-3 h-3" />Visites totales</div>
              </div>
              <div className="bg-card border border-border rounded-xl p-4 text-center">
                <div className="font-display text-2xl font-bold text-green-600">{agentStats.today_visits || 0}</div>
                <div className="text-xs text-muted-foreground mt-1 flex items-center justify-center gap-1"><TrendingUp className="w-3 h-3" />Visites auj.</div>
              </div>
              <div className="bg-card border border-border rounded-xl p-4 text-center">
                <div className="font-display text-2xl font-bold text-amber-600">
                  {fcfa((agentStats.agents || []).reduce((s, a) => s + (a.prime_fcfa || 0), 0))}
                </div>
                <div className="text-xs text-muted-foreground mt-1 flex items-center justify-center gap-1"><Award className="w-3 h-3" />Primes totales</div>
              </div>
            </div>
          )}

          {/* Taux de commission */}
          {agentStats?.commission_rates && (
            <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
              <div className="font-semibold text-amber-800 dark:text-amber-300 text-sm mb-2 flex items-center gap-1"><Award className="w-4 h-4" />Barème des primes</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                {Object.entries(agentStats.commission_rates).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between bg-white dark:bg-card rounded-lg px-3 py-1.5 border border-amber-100 dark:border-border">
                    <span className="text-muted-foreground capitalize">{k.replace("_", " ")}</span>
                    <span className="font-bold text-amber-700 dark:text-amber-400">{v.toLocaleString("fr-FR")} F</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tableau des agents + primes */}
          <div>
            <h3 className="font-semibold text-secondary mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-primary" />Performance & Primes</h3>
            {!agentStats?.agents?.length ? (
              <div className="text-sm text-muted-foreground border border-dashed border-border rounded-xl p-8 text-center">Aucun agent enregistré.</div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="p-3 text-left text-xs font-semibold text-muted-foreground">Agent</th>
                      <th className="p-3 text-center text-xs font-semibold text-muted-foreground">Boutiques</th>
                      <th className="p-3 text-center text-xs font-semibold text-muted-foreground">Restos</th>
                      <th className="p-3 text-center text-xs font-semibold text-muted-foreground">Héberg.</th>
                      <th className="p-3 text-center text-xs font-semibold text-muted-foreground">Transport</th>
                      <th className="p-3 text-center text-xs font-semibold text-muted-foreground">Prestat.</th>
                      <th className="p-3 text-center text-xs font-semibold text-muted-foreground">Total visites</th>
                      <th className="p-3 text-right text-xs font-semibold text-muted-foreground">Prime FCFA</th>
                      <th className="p-3 text-left text-xs font-semibold text-muted-foreground">Dernière visite</th>
                      <th className="p-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(agentStats.agents || []).map((a, i) => (
                      <tr key={a.user_id} className={`border-t border-border ${i % 2 === 0 ? "" : "bg-muted/20"}`}>
                        <td className="p-3">
                          <div className="font-medium text-secondary">{a.name}</div>
                          <div className="text-xs text-muted-foreground">{a.email}</div>
                          {a.phone && <div className="text-xs text-muted-foreground">{a.phone}</div>}
                        </td>
                        <td className="p-3 text-center font-mono">{a.shops || 0}</td>
                        <td className="p-3 text-center font-mono">{a.restaurants || 0}</td>
                        <td className="p-3 text-center font-mono">{a.hebergements || 0}</td>
                        <td className="p-3 text-center font-mono">{a.transports || 0}</td>
                        <td className="p-3 text-center font-mono">{a.providers || 0}</td>
                        <td className="p-3 text-center">
                          <Badge variant="secondary">{a.total_visits || 0}</Badge>
                        </td>
                        <td className="p-3 text-right font-display font-bold text-primary whitespace-nowrap">
                          {(a.prime_fcfa || 0).toLocaleString("fr-FR")} F
                        </td>
                        <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">
                          {a.last_visit
                            ? new Date(a.last_visit).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
                            : "—"}
                        </td>
                        <td className="p-3">
                          <button onClick={() => deactivateAgent(a.user_id, a.name)} title="Désactiver" className="text-muted-foreground hover:text-red-500 transition-colors">
                            <XCircle className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Formulaire création agent */}
          <div className="bg-card border border-border rounded-xl p-5">
            <h3 className="font-semibold text-secondary mb-4 flex items-center gap-2"><UserPlus className="w-4 h-4 text-primary" />Créer un compte agent terrain</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <div>
                <label className="text-xs font-semibold text-muted-foreground mb-1 block">Nom complet *</label>
                <Input value={newAgent.name} onChange={e => setNewAgent(a => ({ ...a, name: e.target.value }))} placeholder="Kouamé Adjoua" />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground mb-1 block">Email *</label>
                <Input type="email" value={newAgent.email} onChange={e => setNewAgent(a => ({ ...a, email: e.target.value }))} placeholder="agent@lmp.ci" />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground mb-1 block">Mot de passe *</label>
                <Input type="password" value={newAgent.password} onChange={e => setNewAgent(a => ({ ...a, password: e.target.value }))} placeholder="Minimum 8 caractères" />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground mb-1 block">Téléphone</label>
                <Input type="tel" value={newAgent.phone} onChange={e => setNewAgent(a => ({ ...a, phone: e.target.value }))} placeholder="+225 07 00 00 00 00" />
              </div>
            </div>
            <Button className="bg-primary hover:bg-primary/90" onClick={createAgent} disabled={agentBusy}>
              <UserPlus className="w-4 h-4 mr-2" />{agentBusy ? "Création…" : "Créer l'agent"}
            </Button>
            <p className="text-xs text-muted-foreground mt-2">L'agent pourra se connecter sur <strong>lmp.sag.ci/lmp</strong> avec son email et mot de passe. Il accédera à l'Espace Agent pour enregistrer des données terrain.</p>
          </div>
        </TabsContent>

        {/* ─── VALIDATION TERRAIN ─── */}
        <TabsContent value="terrain" className="mt-4 space-y-6">
          {brouillons.total === 0 && (
            <div className="text-center py-16 text-muted-foreground border border-dashed border-border rounded-xl">
              <CheckCircle2 className="w-10 h-10 mx-auto mb-3 text-green-500" />
              <p className="font-medium">Aucun brouillon en attente</p>
              <p className="text-sm mt-1">Toutes les saisies terrain ont été traitées.</p>
            </div>
          )}

          {[
            { key: "shops", label: "Boutiques", icon: Store, idKey: "id" },
            { key: "restaurants", label: "Restaurants", icon: UtensilsCrossed, idKey: "id" },
            { key: "hebergements", label: "Hébergements", icon: BedDouble, idKey: "id" },
            { key: "transports", label: "Transports", icon: Car, idKey: "id" },
            { key: "providers", label: "Prestataires", icon: Wrench, idKey: "id" },
            { key: "products", label: "Produits", icon: Package, idKey: "id" },
          ].map(({ key, label, icon: Icon, idKey }) => {
            const items = brouillons[key] || [];
            if (items.length === 0) return null;
            return (
              <Section key={key} title={`${label} (${items.length})`} empty="">
                {items.map((item) => (
                  <div key={item[idKey]} className="bg-card border border-amber-200 rounded-lg p-4 flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center shrink-0 mt-0.5">
                        <Icon className="w-4 h-4 text-amber-600" />
                      </div>
                      <div>
                        <div className="font-semibold text-secondary text-sm">{item.name}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {item.detail && <span className="mr-2">{item.detail}</span>}
                          {item.city && <span className="mr-2">📍 {item.city}</span>}
                          {item.phone && <span>📞 {item.phone}</span>}
                          {item.price && <span className="ml-2 font-semibold text-primary">{item.price?.toLocaleString("fr-FR")} FCFA/{item.unit}</span>}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">{new Date(item.created_at).toLocaleString("fr-FR")}</div>
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white h-8 px-3"
                        onClick={() => validerBrouillon(key.replace(/s$/, ""), item[idKey], "approuver")}>
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Valider
                      </Button>
                      <Button size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 h-8 px-3"
                        onClick={() => validerBrouillon(key.replace(/s$/, ""), item[idKey], "rejeter")}>
                        <XCircle className="w-3.5 h-3.5 mr-1" />Rejeter
                      </Button>
                    </div>
                  </div>
                ))}
              </Section>
            );
          })}
        </TabsContent>

        {/* ─── CANDIDATURES AGENTS ─── */}
        <TabsContent value="candidatures" className="mt-4 space-y-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-muted-foreground">{candidatures.length} candidature(s) reçue(s)</p>
            <div className="flex gap-2 text-xs">
              <span className="px-2 py-1 rounded-full bg-amber-100 text-amber-700 font-semibold">{candidatures.filter(c=>c.statut==="en_attente").length} en attente</span>
              <span className="px-2 py-1 rounded-full bg-green-100 text-green-700 font-semibold">{candidatures.filter(c=>c.statut==="validee").length} validées</span>
              <span className="px-2 py-1 rounded-full bg-red-100 text-red-700 font-semibold">{candidatures.filter(c=>c.statut==="refusee").length} refusées</span>
            </div>
          </div>

          {candidatures.length === 0 && (
            <div className="text-center py-16 text-muted-foreground border border-dashed border-border rounded-xl">
              <ClipboardList className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p>Aucune candidature pour l'instant.</p>
            </div>
          )}

          {candidatures.map((c) => (
            <div key={c.cand_id} className={`bg-card border rounded-xl p-4 ${c.statut === "en_attente" ? "border-blue-200" : c.statut === "validee" ? "border-green-200" : "border-red-200 opacity-60"}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-secondary">{c.name}</span>
                    <Badge className={`text-xs ${c.statut === "en_attente" ? "bg-amber-100 text-amber-700" : c.statut === "validee" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                      {c.statut === "en_attente" ? "En attente" : c.statut === "validee" ? "Validée" : "Refusée"}
                    </Badge>
                  </div>
                  <div className="text-sm text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
                    <span>📞 {c.phone}</span>
                    {c.email && <span>✉️ {c.email}</span>}
                    <span>📍 {c.zone}</span>
                    <span className="text-xs">{new Date(c.created_at).toLocaleDateString("fr-FR")}</span>
                  </div>
                  {c.message && <p className="text-xs text-muted-foreground mt-2 italic border-l-2 border-border pl-2">"{c.message}"</p>}
                  {c.notes_admin && <p className="text-xs text-blue-600 mt-1">Note admin : {c.notes_admin}</p>}
                </div>
                {c.statut === "en_attente" && (
                  <div className="flex gap-2 shrink-0">
                    <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white h-8 px-3"
                      disabled={candBusy[c.cand_id]}
                      onClick={() => updateCandidature(c.cand_id, "validee")}>
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Valider
                    </Button>
                    <a href={`https://wa.me/${c.phone?.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer">
                      <Button size="sm" variant="outline" className="h-8 px-3 border-green-300 text-green-700 hover:bg-green-50">
                        💬 WhatsApp
                      </Button>
                    </a>
                    <Button size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 h-8 px-3"
                      disabled={candBusy[c.cand_id]}
                      onClick={() => updateCandidature(c.cand_id, "refusee")}>
                      <XCircle className="w-3.5 h-3.5 mr-1" />Refuser
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </TabsContent>
      </Tabs>

      {/* ─── MODAL DÉTAIL MODÉRATION ─── */}
      {detailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setDetailModal(null)}>
          <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h2 className="font-semibold text-secondary text-lg">
                {detailModal.type === "shop" ? "Fiche Boutique" : "Fiche Prestataire"}
              </h2>
              <button onClick={() => setDetailModal(null)} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 space-y-4">
              {detailModal.type === "shop" ? (
                <ShopDetailContent shop={detailModal.entity} products={detailModal.products} />
              ) : (
                <ProviderDetailContent provider={detailModal.entity} />
              )}
              <div className="flex gap-2 pt-2 border-t border-border">
                <Button className="flex-1 bg-accent hover:bg-accent/90 text-accent-foreground" onClick={() => {
                  const e = detailModal.entity;
                  detailModal.type === "shop" ? decide("shop", e.shop_id, "approved") : decide("provider", e.provider_id, "approved");
                  setDetailModal(null);
                }}><CheckCircle2 className="w-4 h-4 mr-1" />Valider</Button>
                <Button variant="outline" className="flex-1" onClick={() => {
                  const e = detailModal.entity;
                  detailModal.type === "shop" ? decide("shop", e.shop_id, "rejected") : decide("provider", e.provider_id, "rejected");
                  setDetailModal(null);
                }}><XCircle className="w-4 h-4 mr-1" />Refuser</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, children, empty }) {
  const items = React.Children.toArray(children);
  return (
    <div>
      <h3 className="font-semibold text-secondary mb-3 flex items-center gap-1">{title}</h3>
      {items.length === 0 ? (
        <div className="text-sm text-muted-foreground border border-dashed border-border rounded-lg p-6 text-center">{empty}</div>
      ) : (
        <div className="space-y-2">{items}</div>
      )}
    </div>
  );
}

function Row({ title, sub, onApprove, onReject, tid }) {
  return (
    <div className="bg-card border border-border rounded-lg p-4 flex items-center justify-between">
      <div><div className="font-medium text-secondary">{title}</div><div className="text-xs text-muted-foreground">{sub}</div></div>
      <div className="flex gap-2">
        <Button size="sm" className="bg-accent hover:bg-accent/90 text-accent-foreground" onClick={onApprove}><CheckCircle2 className="w-4 h-4 mr-1" />Valider</Button>
        <Button size="sm" variant="outline" onClick={onReject}><XCircle className="w-4 h-4 mr-1" />Refuser</Button>
      </div>
    </div>
  );
}

function PendingShopCard({ shop: s, onApprove, onReject, onDetail }) {
  return (
    <div className="bg-card border border-border rounded-lg p-4 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-3">
          {s.logo_url && <img src={s.logo_url} alt="" className="w-12 h-12 rounded-lg object-cover border border-border shrink-0" onError={(e) => { e.target.style.display="none"; }} />}
          <div>
            <div className="font-semibold text-secondary">{s.name || "—"}</div>
            <div className="text-xs text-muted-foreground">{[s.category, s.city].filter(Boolean).join(" · ") || "—"}</div>
          </div>
        </div>
        <Badge variant="outline" className="text-amber-600 border-amber-400 text-xs shrink-0">En attente</Badge>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
        {s.address && <span><span className="font-medium text-foreground">Adresse :</span> {s.address}</span>}
        {s.phone && <span><span className="font-medium text-foreground">Tél :</span> {s.phone}</span>}
        {s.hours && <span><span className="font-medium text-foreground">Horaires :</span> {s.hours}</span>}
        {s.created_at && <span><span className="font-medium text-foreground">Créé le :</span> {new Date(s.created_at).toLocaleDateString("fr-FR")}</span>}
      </div>
      {s.description && <p className="text-xs text-muted-foreground italic line-clamp-2">{s.description}</p>}
      <div className="flex gap-2 pt-1">
        <Button size="sm" className="bg-accent hover:bg-accent/90 text-accent-foreground" onClick={onApprove}><CheckCircle2 className="w-4 h-4 mr-1" />Valider</Button>
        <Button size="sm" variant="outline" onClick={onReject}><XCircle className="w-4 h-4 mr-1" />Refuser</Button>
        <Button size="sm" variant="ghost" className="ml-auto text-primary" onClick={onDetail}><Search className="w-4 h-4 mr-1" />Voir la fiche</Button>
      </div>
    </div>
  );
}

function PendingProviderCard({ provider: p, onApprove, onReject, onDetail }) {
  return (
    <div className="bg-card border border-border rounded-lg p-4 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-3">
          {p.photo_url && <img src={p.photo_url} alt="" className="w-12 h-12 rounded-full object-cover border border-border shrink-0" onError={(e) => { e.target.style.display="none"; }} />}
          <div>
            <div className="font-semibold text-secondary">{p.name || "—"}</div>
            <div className="text-xs text-muted-foreground">{[p.profession, p.city].filter(Boolean).join(" · ") || "—"}</div>
          </div>
        </div>
        <Badge variant="outline" className="text-amber-600 border-amber-400 text-xs shrink-0">En attente</Badge>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
        {p.phone && <span><span className="font-medium text-foreground">Tél :</span> {p.phone}</span>}
        {p.base_rate && <span><span className="font-medium text-foreground">Tarif :</span> {p.base_rate?.toLocaleString("fr-FR")} FCFA</span>}
        {p.zones?.length > 0 && <span><span className="font-medium text-foreground">Zones :</span> {p.zones.join(", ")}</span>}
        {p.created_at && <span><span className="font-medium text-foreground">Créé le :</span> {new Date(p.created_at).toLocaleDateString("fr-FR")}</span>}
      </div>
      {p.bio && <p className="text-xs text-muted-foreground italic line-clamp-2">{p.bio}</p>}
      <div className="flex gap-2 pt-1">
        <Button size="sm" className="bg-accent hover:bg-accent/90 text-accent-foreground" onClick={onApprove}><CheckCircle2 className="w-4 h-4 mr-1" />Valider</Button>
        <Button size="sm" variant="outline" onClick={onReject}><XCircle className="w-4 h-4 mr-1" />Refuser</Button>
        <Button size="sm" variant="ghost" className="ml-auto text-primary" onClick={onDetail}><Search className="w-4 h-4 mr-1" />Voir la fiche</Button>
      </div>
    </div>
  );
}

function ShopDetailContent({ shop: s, products }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        {s.logo_url && <img src={s.logo_url} alt="" className="w-16 h-16 rounded-lg object-cover border border-border" onError={(e) => { e.target.style.display="none"; }} />}
        <div>
          <div className="font-bold text-secondary text-xl">{s.name}</div>
          <div className="text-sm text-muted-foreground">{[s.category, s.city].filter(Boolean).join(" · ")}</div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm">
        {[
          ["Adresse", s.address], ["Téléphone", s.phone], ["Horaires", s.hours],
          ["Créé le", s.created_at && new Date(s.created_at).toLocaleDateString("fr-FR")],
        ].filter(([, v]) => v).map(([label, val]) => (
          <div key={label}><div className="text-xs text-muted-foreground">{label}</div><div className="font-medium">{val}</div></div>
        ))}
      </div>
      {s.description && <p className="text-sm text-muted-foreground border-l-2 border-primary pl-3">{s.description}</p>}
      <div>
        <div className="font-semibold text-secondary mb-2">Produits ({products.length})</div>
        {products.length === 0 ? (
          <div className="text-sm text-muted-foreground italic">Aucun produit enregistré</div>
        ) : (
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {products.map((p) => (
              <div key={p.product_id} className="flex items-center gap-3 p-2 rounded-lg bg-muted/40">
                {p.image_url && <img src={p.image_url} alt="" className="w-10 h-10 rounded object-cover border border-border shrink-0" onError={(e) => { e.target.style.display="none"; }} />}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{p.name}</div>
                  <div className="text-xs text-muted-foreground">{p.category}</div>
                </div>
                <div className="text-right text-xs shrink-0">
                  {p.prix_detail && <div>Détail : {p.prix_detail?.toLocaleString("fr-FR")} FCFA</div>}
                  {p.prix_demi_gros && <div>½ gros : {p.prix_demi_gros?.toLocaleString("fr-FR")} FCFA</div>}
                  {p.prix_gros && <div>Gros : {p.prix_gros?.toLocaleString("fr-FR")} FCFA</div>}
                  {!p.prix_detail && !p.prix_demi_gros && !p.prix_gros && p.price && <div>{p.price?.toLocaleString("fr-FR")} FCFA</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ProviderDetailContent({ provider: p }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        {p.photo_url && <img src={p.photo_url} alt="" className="w-16 h-16 rounded-full object-cover border border-border" onError={(e) => { e.target.style.display="none"; }} />}
        <div>
          <div className="font-bold text-secondary text-xl">{p.name}</div>
          <div className="text-sm text-muted-foreground">{[p.profession, p.city].filter(Boolean).join(" · ")}</div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm">
        {[
          ["Téléphone", p.phone], ["Tarif de base", p.base_rate && `${p.base_rate?.toLocaleString("fr-FR")} FCFA`],
          ["Zones d'intervention", p.zones?.join(", ")],
          ["Services", p.services?.join(", ")],
          ["Créé le", p.created_at && new Date(p.created_at).toLocaleDateString("fr-FR")],
        ].filter(([, v]) => v).map(([label, val]) => (
          <div key={label}><div className="text-xs text-muted-foreground">{label}</div><div className="font-medium">{val}</div></div>
        ))}
      </div>
      {p.bio && <p className="text-sm text-muted-foreground border-l-2 border-primary pl-3">{p.bio}</p>}
    </div>
  );
}
